// create / show / edit / revoke a bus's parent link; with School Bus Standard, the bus's driver roll-call link too.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, KeyRound, Printer, RefreshCw, Trash2 } from 'lucide-react';
import { busLinks } from '@/lib/trackizy';
import { showToast } from '@/components/shared/Toast';
import useBranding from '@/hooks/useBranding';
import useFeatures from '@/hooks/useFeatures';
import WindowsEditor, { DEFAULT_WINDOWS } from './WindowsEditor';
import { printSheet } from './PrintSheet';
import { markerSvg } from './busIcon';
import { apiErrorCode } from '@/features/school-bus/studentImport';

const SWATCHES = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#0891b2', '#db2777', '#475569'];

const btn = 'inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-xs disabled:opacity-50';
// Built from the browser's origin like the parent link (APP_URL is not plumbed on every box); keeps the server's path.
const onThisOrigin = (u) => { try { return `${window.location.origin}${new URL(u).pathname}`; } catch { return u; } };

/** Driver roll-call link for this bus: issue / rotate (URL shown once), revoke, 4-digit PIN, roll-call switch. */
function DriverLinkSection({ link, onChange }) {
  const { t } = useTranslation();
  const brand = useBranding();
  const [issued, setIssued] = useState(null); // the URL, shown once after issue / rotate
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  const issue = () => {
    if (link.hasDriverLink && !window.confirm(t('parentLink.driverRotateConfirm'))) return;
    act(async () => {
      try {
        const r = await busLinks.driverLink.issue(link.id);
        setIssued(onThisOrigin(r.url));
        onChange({ hasDriverLink: true, hasDriverPin: false });
      } catch { showToast(t('parentLink.error')); }
    });
  };
  const revoke = () => {
    if (!window.confirm(t('parentLink.driverRevokeConfirm'))) return;
    act(async () => {
      try {
        await busLinks.driverLink.revoke(link.id);
        setIssued(null);
        onChange({ hasDriverLink: false, hasDriverPin: false });
        showToast(t('parentLink.driverRevoked'));
      } catch { showToast(t('parentLink.error')); }
    });
  };
  const savePin = (value) => act(async () => {
    try {
      await busLinks.driverLink.setPin(link.id, value);
      setPin('');
      onChange({ hasDriverPin: Boolean(value) });
      showToast(value ? t('parentLink.driverPinSet') : t('parentLink.driverPinCleared'));
    } catch (e) {
      const code = apiErrorCode(e);
      showToast(code === 'invalid_pin' ? t('parentLink.driverPinInvalid') : code === 'no_link' ? t('parentLink.driverPinNoLink') : t('parentLink.error'));
    }
  });
  const setRollCall = (rollCall) => act(async () => {
    try {
      const l = await busLinks.update(link.id, { rollCall });
      onChange({ rollCall: typeof l?.rollCall === 'boolean' ? l.rollCall : rollCall });
    } catch { showToast(t('parentLink.error')); }
  });
  const copy = async () => { try { await navigator.clipboard.writeText(issued); showToast(t('parentLink.copied')); } catch { showToast(t('parentLink.error')); } };
  const print = () => {
    if (!printSheet({ url: issued, label: link.label, schoolName: brand.name, instruction: t('parentLink.driverPrintInstruction'), color: link.color || brand.color })) showToast(t('parentLink.popupBlocked'));
  };

  return (
    <section data-testid="driver-link" className="space-y-3 rounded-2xl border border-slate-200 dark:border-slate-700 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t('parentLink.driverTitle')}</h3>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={link.rollCall !== false} disabled={busy} onChange={(e) => setRollCall(e.target.checked)} />{t('parentLink.driverRollCall')}
        </label>
      </div>
      <p className="text-xs text-slate-500">
        {link.hasDriverLink ? t('parentLink.driverActive') : t('parentLink.driverNone')}
        {link.hasDriverLink && <> · {link.hasDriverPin ? t('parentLink.driverHasPin') : t('parentLink.driverNoPin')}</>}
      </p>
      {issued && (
        <div data-testid="driver-issued" className="flex gap-3 items-start rounded-xl bg-blue-50/60 dark:bg-blue-500/10 p-2">
          <div className="bg-white p-2 rounded-xl border border-slate-200"><QRCodeSVG value={issued} size={96} /></div>
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">{t('parentLink.driverShownOnce')}</p>
            <div dir="ltr" className="text-xs font-mono break-all text-start text-slate-600 dark:text-slate-300">{issued}</div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={copy} className={btn}><Copy size={12} />{t('parentLink.copy')}</button>
              <button type="button" onClick={print} className={btn}><Printer size={12} />{t('parentLink.print')}</button>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={issue} className={btn}><RefreshCw size={12} />{link.hasDriverLink ? t('parentLink.driverRotate') : t('parentLink.driverIssue')}</button>
        {link.hasDriverLink && <button type="button" disabled={busy} onClick={revoke} className={`${btn} text-red-600`}><Trash2 size={12} />{t('parentLink.driverRevoke')}</button>}
      </div>
      {link.hasDriverLink && (
        <div className="flex flex-wrap items-center gap-2">
          <KeyRound size={14} className="text-slate-400" />
          <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" autoComplete="off" dir="ltr" maxLength={4}
            aria-label={t('parentLink.driverPin')} placeholder="0000" className="w-20 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-2 py-1 text-sm tracking-widest" />
          <button type="button" disabled={busy || pin.length !== 4} onClick={() => savePin(pin)} className={btn}>{t('parentLink.driverPinSave')}</button>
          {link.hasDriverPin && <button type="button" disabled={busy} onClick={() => savePin(null)} className={btn}>{t('parentLink.driverPinClear')}</button>}
          <span className="basis-full text-[11px] text-slate-500">{t('parentLink.driverPinHint')}</span>
        </div>
      )}
    </section>
  );
}

const toForm = (l) => l ? { label: l.label, windows: l.windows, contactPhone: l.contactPhone || '', showDriver: l.showDriver, enabled: l.enabled, expiresAt: l.expiresAt ? l.expiresAt.slice(0, 10) : '', announcement: l.announcement || '', color: l.color || '' } : null;

export default function ParentLinkTab({ deviceId, deviceName, deviceAttributes }) {
  const { t } = useTranslation();
  const brand = useBranding();
  const { schoolBusStandard } = useFeatures();
  const timezone = useSelector((s) => s.org?.settings?.attendance?.timezone) || 'Asia/Baghdad';
  const [link, setLink] = useState(undefined); // undefined=loading, null=none
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const reqRef = useRef(0);
  const load = useCallback(() => {
    const req = ++reqRef.current;
    setLink(undefined); setForm(null);
    busLinks.byDevice(deviceId)
      .then((l) => { if (req === reqRef.current) { setLink(l); setForm(toForm(l)); } })
      .catch((e) => { if (req === reqRef.current) { if (e?.status === 404) { setLink(null); setForm(null); } else setLink('error'); } });
  }, [deviceId]);
  useEffect(() => { load(); return () => { reqRef.current++; }; }, [load]);

  const fromForm = (f) => ({ label: f.label, windows: f.windows, contactPhone: f.contactPhone.trim() || null, showDriver: f.showDriver, enabled: f.enabled, expiresAt: f.expiresAt ? new Date(`${f.expiresAt}T23:59:59`).toISOString() : null, announcement: f.announcement.trim() || null, color: f.color || null });

  const run = async (fn, okKey) => { setBusy(true); try { const l = await fn(); setLink(l); setForm(toForm(l)); if (okKey) showToast(t(okKey), 'success'); } catch { showToast(t('parentLink.error'), 'error'); } finally { setBusy(false); } };

  if (link === 'error') {
    return (
      <div className="space-y-3">
        <div className="text-sm text-red-600">{t('parentLink.error')}</div>
        <button type="button" onClick={load} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm">{t('parentLink.retry')}</button>
      </div>
    );
  }
  if (link === undefined) return <div className="text-sm text-slate-500">{t('common.loading')}</div>;

  if (link === null) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">{t('parentLink.intro')}</p>
        <div className="text-xs text-slate-500">{t('parentLink.windowsHint', { timezone })}</div>
        <div className="text-xs text-slate-600 dark:text-slate-300">{t('parentLink.defaultWindows')}</div>
        <button disabled={busy} onClick={() => run(() => busLinks.create({ deviceId, label: deviceName, windows: DEFAULT_WINDOWS }), 'parentLink.created')}
          className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold">{t('parentLink.create')}</button>
      </div>
    );
  }

  // Built from the browser's origin, never the server's `url` (APP_URL is not plumbed on every box).
  const url = `${window.location.origin}/bus/${link.token}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); showToast(t('parentLink.copied'), 'success'); } catch { showToast(t('parentLink.error'), 'error'); } };
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(link));

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-start">
        <div className="flex flex-col items-center gap-2">
          <div className="bg-white p-2 rounded-xl border border-slate-200"><QRCodeSVG value={url} size={112} /></div>
          <div className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-white [&_svg]:w-5 [&_svg]:h-5" style={{ backgroundColor: form.color || brand.color }} dangerouslySetInnerHTML={{ __html: markerSvg(deviceAttributes?.mapMarkerIcon) }} />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="text-xs font-mono break-all text-slate-600 dark:text-slate-300">{url}</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copy} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-xs"><Copy size={12} />{t('parentLink.copy')}</button>
            <button onClick={() => { if (!printSheet({ url, label: link.label, schoolName: brand.name, instruction: t('parentLink.printInstruction'), color: form.color || brand.color })) showToast(t('parentLink.popupBlocked'), 'error'); }} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-xs"><Printer size={12} />{t('parentLink.print')}</button>
            <button disabled={busy} onClick={() => window.confirm(t('parentLink.regenerateConfirm')) && run(() => busLinks.regenerate(link.id), 'parentLink.regenerated')} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-xs"><RefreshCw size={12} />{t('parentLink.newLink')}</button>
            <button disabled={busy} onClick={() => window.confirm(t('parentLink.deleteConfirm')) && run(async () => { await busLinks.remove(link.id); return null; }, 'parentLink.deleted')} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-300 dark:border-slate-600 text-xs text-red-600"><Trash2 size={12} />{t('common.delete')}</button>
          </div>
        </div>
      </div>

      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">{t('parentLink.label')}
        <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm" />
      </label>
      <div>
        <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">{t('parentLink.windows')}</div>
        <WindowsEditor value={form.windows} onChange={(windows) => setForm({ ...form, windows })} timezone={timezone} />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">{t('parentLink.announcement')}</span>
          <button type="button" onClick={() => setForm({ ...form, announcement: '' })} className="text-xs text-blue-600">{t('common.clear')}</button>
        </div>
        <textarea rows={3} maxLength={300} value={form.announcement} onChange={(e) => setForm({ ...form, announcement: e.target.value })} className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm" />
        <div className="flex justify-between text-[11px] text-slate-500 mt-0.5"><span>{t('parentLink.announcementHint')}</span><span dir="ltr">{`${form.announcement.length}/300`}</span></div>
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">{t('parentLink.color')}</div>
        <div className="flex flex-wrap items-center gap-2">
          {SWATCHES.map((c) => (
            <button key={c} type="button" aria-label={c} onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-full ${form.color === c ? 'ring-2 ring-offset-2 ring-slate-900 dark:ring-white' : ''}`} style={{ backgroundColor: c }} />
          ))}
          <button type="button" onClick={() => setForm({ ...form, color: '' })} className={`px-2 py-1 rounded-full border text-xs ${form.color === '' ? 'ring-2 ring-offset-2 ring-slate-900 dark:ring-white' : 'border-slate-300 dark:border-slate-600'}`}>{t('parentLink.brandColor')}</button>
        </div>
      </div>
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">{t('parentLink.contactPhone')}
        <input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} placeholder="+964…" className="mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm" dir="ltr" />
      </label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.showDriver} onChange={(e) => setForm({ ...form, showDriver: e.target.checked })} />{t('parentLink.showDriver')}</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />{t('parentLink.enabled')}</label>
      <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">{t('parentLink.expiresAt')}
        <input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} className="mt-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm" />
      </label>
      <button disabled={busy || !dirty} onClick={() => run(() => busLinks.update(link.id, fromForm(form)), 'parentLink.saved')} className="px-4 py-2 rounded-xl bg-blue-600 disabled:opacity-50 text-white text-sm font-semibold">{t('common.save')}</button>
      {schoolBusStandard && <DriverLinkSection link={link} onChange={(patch) => setLink((l) => ({ ...l, ...patch }))} />}
    </div>
  );
}